import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import type { ExamName } from '../examTasks/examTaskTypes';
import {
    deleteVocabularyBook,
    deleteVocabularyWord,
    getVocabularyBooks,
    getVocabularyWords,
    saveVocabularyBook,
    saveVocabularyWord,
    type AdminVocabularyBook,
    type AdminVocabularyBookInput,
    type AdminVocabularyWord,
    type AdminVocabularyWordInput,
} from '../admin/adminVocabularyApi';

const emptyBook: AdminVocabularyBookInput = {
    examName: 'Academic',
    title: '',
    description: '',
    coverKey: 'custom',
    coverColor: '#22c7ba',
    displayOrder: 0,
    isActive: true,
};

const emptyWord: AdminVocabularyWordInput = {
    word: '',
    isActive: true,
};

export function VocabularyManagementPage() {
    const [books, setBooks] = useState<AdminVocabularyBook[]>([]);
    const [bookTotal, setBookTotal] = useState(0);
    const [bookPage, setBookPage] = useState(0);
    const [bookPageSize, setBookPageSize] = useState(10);
    const [bookSearch, setBookSearch] = useState('');
    const [bookExamName, setBookExamName] = useState<ExamName | ''>('');
    const [selectedBook, setSelectedBook] = useState<AdminVocabularyBook | null>(null);
    const [bookInput, setBookInput] = useState<AdminVocabularyBookInput>(emptyBook);
    const [words, setWords] = useState<AdminVocabularyWord[]>([]);
    const [wordTotal, setWordTotal] = useState(0);
    const [wordPage, setWordPage] = useState(0);
    const [wordPageSize, setWordPageSize] = useState(10);
    const [wordSearch, setWordSearch] = useState('');
    const [selectedWord, setSelectedWord] = useState<AdminVocabularyWord | null>(null);
    const [wordInput, setWordInput] = useState<AdminVocabularyWordInput>(emptyWord);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    async function loadBooks() {
        setLoading(true);
        setError(null);
        try {
            const result = await getVocabularyBooks({
                page: bookPage + 1,
                pageSize: bookPageSize,
                search: bookSearch,
                examName: bookExamName,
            });
            setBooks(result.items);
            setBookTotal(result.totalCount);
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Failed to load books.');
        } finally {
            setLoading(false);
        }
    }

    async function loadWords(bookId = selectedBook?.bookId) {
        if (!bookId) {
            setWords([]);
            setWordTotal(0);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await getVocabularyWords(bookId, {
                page: wordPage + 1,
                pageSize: wordPageSize,
                search: wordSearch,
            });
            setWords(result.items);
            setWordTotal(result.totalCount);
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Failed to load words.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        void loadBooks();
    }, [bookPage, bookPageSize, bookExamName]);

    useEffect(() => {
        void loadWords();
    }, [selectedBook?.bookId, wordPage, wordPageSize]);

    function editBook(book: AdminVocabularyBook) {
        setSelectedBook(book);
        setBookInput({
            examName: book.examName,
            title: book.title,
            description: book.description,
            coverKey: book.coverKey,
            coverColor: book.coverColor,
            displayOrder: book.displayOrder,
            isActive: book.isActive,
        });
        setSelectedWord(null);
        setWordInput(emptyWord);
        setWordPage(0);
    }

    function editWord(word: AdminVocabularyWord) {
        setSelectedWord(word);
        setWordInput({
            word: word.word,
            isActive: word.isActive,
        });
    }

    async function submitBook() {
        setError(null);
        setMessage(null);
        try {
            const saved = await saveVocabularyBook(bookInput, selectedBook?.bookId);
            setMessage('Book saved.');
            editBook(saved);
            await loadBooks();
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Book save failed.');
        }
    }

    async function submitWord() {
        if (!selectedBook) {
            setError('Select a book before adding words.');
            return;
        }
        setError(null);
        setMessage(null);
        try {
            const saved = await saveVocabularyWord(selectedBook.bookId, wordInput, selectedWord?.wordId);
            setMessage('Word saved.');
            editWord(saved);
            await loadWords(selectedBook.bookId);
            await loadBooks();
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Word save failed.');
        }
    }

    async function removeBook(book: AdminVocabularyBook) {
        setError(null);
        setMessage(null);
        try {
            await deleteVocabularyBook(book.bookId);
            setMessage('Book deactivated.');
            if (selectedBook?.bookId === book.bookId) {
                setSelectedBook(null);
                setBookInput(emptyBook);
            }
            await loadBooks();
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Book delete failed.');
        }
    }

    async function removeWord(word: AdminVocabularyWord) {
        if (!selectedBook) return;
        setError(null);
        setMessage(null);
        try {
            await deleteVocabularyWord(selectedBook.bookId, word.wordId);
            setMessage('Word deactivated.');
            setSelectedWord(null);
            setWordInput(emptyWord);
            await loadWords(selectedBook.bookId);
            await loadBooks();
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Word delete failed.');
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <AutoStoriesOutlinedIcon color="primary" />
                <Typography variant="h4" fontWeight={800}>Vocabulary</Typography>
            </Stack>
            {message ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert> : null}
            {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}

            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} alignItems="flex-start">
                <Paper sx={{ p: 2, width: { xs: '100%', xl: '58%' }, borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                        <TextField label="Search books" value={bookSearch} onChange={(e) => setBookSearch(e.target.value)} fullWidth />
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel>Exam</InputLabel>
                            <Select label="Exam" value={bookExamName} onChange={(e) => setBookExamName(e.target.value as ExamName | '')}>
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Academic">Academic</MenuItem>
                                <MenuItem value="Core">Core</MenuItem>
                            </Select>
                        </FormControl>
                        <Button variant="contained" disabled={loading} onClick={() => { setBookPage(0); void loadBooks(); }}>Search</Button>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Book</TableCell>
                                <TableCell>Exam</TableCell>
                                <TableCell>Words</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {books.map((book) => (
                                <TableRow key={book.bookId} hover selected={selectedBook?.bookId === book.bookId}>
                                    <TableCell>
                                        <Typography fontWeight={700}>{book.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">{book.description}</Typography>
                                    </TableCell>
                                    <TableCell>{book.examName}</TableCell>
                                    <TableCell>{book.wordCount}</TableCell>
                                    <TableCell><Chip size="small" label={book.isActive ? 'Active' : 'Inactive'} color={book.isActive ? 'success' : 'default'} /></TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => editBook(book)}>Edit</Button>
                                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => void removeBook(book)}>Delete</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination component="div" count={bookTotal} page={bookPage} rowsPerPage={bookPageSize} onPageChange={(_, page) => setBookPage(page)} onRowsPerPageChange={(e) => { setBookPageSize(Number(e.target.value)); setBookPage(0); }} />
                </Paper>

                <Paper sx={{ p: 2, width: { xs: '100%', xl: '42%' }, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={800}>{selectedBook ? 'Edit Book' : 'Create Book'}</Typography>
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Exam</InputLabel>
                            <Select label="Exam" value={bookInput.examName} onChange={(e) => setBookInput((value) => ({ ...value, examName: e.target.value as ExamName }))}>
                                <MenuItem value="Academic">Academic</MenuItem>
                                <MenuItem value="Core">Core</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Title" value={bookInput.title} onChange={(e) => setBookInput((value) => ({ ...value, title: e.target.value }))} />
                        <TextField label="Description" value={bookInput.description} onChange={(e) => setBookInput((value) => ({ ...value, description: e.target.value }))} multiline minRows={2} />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Cover key" value={bookInput.coverKey} onChange={(e) => setBookInput((value) => ({ ...value, coverKey: e.target.value }))} fullWidth />
                            <TextField label="Color" value={bookInput.coverColor} onChange={(e) => setBookInput((value) => ({ ...value, coverColor: e.target.value }))} sx={{ width: 130 }} />
                        </Stack>
                        <TextField label="Order" type="number" value={bookInput.displayOrder} onChange={(e) => setBookInput((value) => ({ ...value, displayOrder: Number(e.target.value) }))} />
                        <Stack direction="row" alignItems="center" spacing={1}><Switch checked={bookInput.isActive} onChange={(e) => setBookInput((value) => ({ ...value, isActive: e.target.checked }))} /><Typography>Active</Typography></Stack>
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" startIcon={<SaveOutlinedIcon />} disabled={!bookInput.title.trim()} onClick={() => void submitBook()}>Save Book</Button>
                            <Button onClick={() => { setSelectedBook(null); setBookInput(emptyBook); }}>New Book</Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>

            <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={800}>Words: {selectedBook?.title ?? 'Select a book'}</Typography>
                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems="flex-start">
                    <Box sx={{ width: { xs: '100%', xl: '58%' } }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                            <TextField label="Search words" value={wordSearch} onChange={(e) => setWordSearch(e.target.value)} fullWidth disabled={!selectedBook} />
                            <Button variant="contained" disabled={!selectedBook || loading} onClick={() => { setWordPage(0); void loadWords(); }}>Search</Button>
                        </Stack>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Word</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {words.map((word) => (
                                    <TableRow key={word.wordId} hover selected={selectedWord?.wordId === word.wordId}>
                                        <TableCell><Typography fontWeight={700}>{word.word}</Typography></TableCell>
                                        <TableCell><Chip size="small" label={word.isActive ? 'Active' : 'Inactive'} color={word.isActive ? 'success' : 'default'} /></TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => editWord(word)}>Edit</Button>
                                            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => void removeWord(word)}>Delete</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination component="div" count={wordTotal} page={wordPage} rowsPerPage={wordPageSize} onPageChange={(_, page) => setWordPage(page)} onRowsPerPageChange={(e) => { setWordPageSize(Number(e.target.value)); setWordPage(0); }} />
                    </Box>
                    <Box sx={{ width: { xs: '100%', xl: '42%' } }}>
                        <Typography variant="subtitle1" fontWeight={800}>{selectedWord ? 'Edit Word' : 'Add Word'}</Typography>
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                            <TextField label="Word" value={wordInput.word} onChange={(e) => setWordInput((value) => ({ ...value, word: e.target.value }))} disabled={!selectedBook} />
                            <Alert severity="info">Definitions, examples, and phonetics are loaded from the configured Kaikki dictionary database.</Alert>
                            <Stack direction="row" alignItems="center" spacing={1}><Switch checked={wordInput.isActive} onChange={(e) => setWordInput((value) => ({ ...value, isActive: e.target.checked }))} disabled={!selectedBook} /><Typography>Active</Typography></Stack>
                            <Stack direction="row" spacing={1}>
                                <Button variant="contained" startIcon={<SaveOutlinedIcon />} disabled={!selectedBook || !wordInput.word.trim()} onClick={() => void submitWord()}>Save Word</Button>
                                <Button disabled={!selectedBook} onClick={() => { setSelectedWord(null); setWordInput(emptyWord); }}>New Word</Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </Paper>
        </Box>
    );
}
